import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { User, Store, Shield, Wallet, LogOut, ChevronRight, Gift, MessageCircle, Headphones, Building2, Zap, TrendingUp, BadgeCheck } from 'lucide-react';

export default function Profile() {
  const { user, logout } = useAuth();
  if (!user) return null;

  const kybColor = { none:'#6B6B8A', pending:'#F5A623', approved:'#00C864', rejected:'#FF3232' };

  const menuItems = [
    { icon:Wallet,      label:'My Wallet',         sub:'Deposit, withdraw, balance',            to:'/wallet',           color:'#FF6B00' },
    { icon:Store,       label:'My Orders',          sub:'Track purchases',                        to:'/orders',           color:'#4DA2FF' },
    { icon:Gift,        label:'Reward Points',      sub:`${user.loyaltyPoints||0} pts · Future coin`, to:'/rewards',      color:'#F5A623' },
    { icon:MessageCircle, label:'Support',          sub:'Get help with orders or account',       to:'/support',          color:'#9945FF' },
    { icon:Zap,         label:'Top Up & Bills',     sub:'Airtime, data, electricity, TV',        to:'/vtu',              color:'#00C864' },
    ...(user.role === 'seller' ? [
      { icon:Store,     label:'Seller Dashboard',   sub:'Manage products and orders',            to:'/seller',           color:'#FF6B00' },
      { icon:Building2, label:'My Earnings',        sub:'USDT wallet · Withdraw to bank',        to:'/seller/wallet',    color:'#26A17B' },
      { icon:TrendingUp,label:'Analytics',          sub:'Sales charts and top products',         to:'/seller/analytics', color:'#4DA2FF' },
      { icon:BadgeCheck,label:`KYB: ${user.kyb?.status||'Not started'}`, sub:'Business verification', to:'/kyb',          color:kybColor[user.kyb?.status||'none'] },
    ] : []),
    ...(user.role === 'cs_staff' ? [
      { icon:Headphones, label:'CS Dashboard',      sub:'Handle support tickets',                to:'/cs',               color:'#4DA2FF' },
    ] : []),
    ...(user.role === 'admin' ? [
      { icon:Shield,    label:'Admin Panel',        sub:'Full platform control',                 to:'/admin',            color:'#FF6B00' },
      { icon:Headphones,label:'CS Dashboard',       sub:'View all support tickets',              to:'/cs',               color:'#4DA2FF' },
    ] : []),
  ];

  return (
    <div className="min-h-screen pt-20 pb-24 px-4 max-w-lg mx-auto">
      {/* Avatar */}
      <div className="mt-6 mb-6 text-center">
        <div className="w-16 h-16 rounded-2xl mx-auto mb-3 flex items-center justify-center text-2xl font-bold text-white"
          style={{ background:'linear-gradient(135deg,#FF6B00,#F5A623)', fontFamily:'Sora' }}>
          {user.username?.[0]?.toUpperCase()}
        </div>
        <h1 className="text-xl font-bold" style={{ fontFamily:'Sora' }}>{user.username}</h1>
        <p className="text-brand-muted text-sm">{user.email}</p>
        <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
          <span className="badge badge-orange capitalize">{user.role}</span>
          {user.loyaltyPoints > 0 && <span className="badge badge-yellow">🎁 {user.loyaltyPoints} pts</span>}
          {user.role === 'seller' && user.kyb?.status === 'approved' && <span className="badge" style={{ background:'rgba(0,200,100,0.1)', color:'#00C864', border:'1px solid rgba(0,200,100,0.3)' }}>✅ Verified</span>}
        </div>
      </div>

      <div className="space-y-2">
        {menuItems.map(({ icon:Icon, label, sub, to, color }) => (
          <Link key={to} to={to}
            className="flex items-center gap-3 p-4 rounded-2xl transition-colors hover:bg-white/5"
            style={{ background:'#12121A', border:'1px solid #1E1E2E' }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background:`${color}15` }}>
              <Icon size={18} style={{ color }} />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm" style={{ color }}>{label}</p>
              <p className="text-xs text-brand-muted">{sub}</p>
            </div>
            <ChevronRight size={16} className="text-brand-muted" />
          </Link>
        ))}

        <button onClick={logout}
          className="w-full flex items-center gap-3 p-4 rounded-2xl hover:bg-white/5 transition-colors"
          style={{ background:'#12121A', border:'1px solid rgba(255,50,50,0.3)' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background:'rgba(255,50,50,0.1)' }}>
            <LogOut size={18} className="text-red-400" />
          </div>
          <span className="font-semibold text-sm text-red-400">Sign Out</span>
        </button>
      </div>

      {/* Wallet addresses */}
      {user.wallet && (
        <div className="mt-6 glass rounded-2xl p-4">
          <h3 className="font-bold mb-3 text-sm" style={{ fontFamily:'Sora' }}>My Deposit Addresses</h3>
          {[['EVM (ETH/BNB/USDT)', user.wallet.evmAddress], ['Solana', user.wallet.solAddress], ['Bitcoin', user.wallet.btcAddress]].map(([label, addr]) => (
            <div key={label} className="mb-2">
              <p className="text-xs text-brand-muted">{label}</p>
              <p className="font-mono text-xs break-all" style={{ color:'#E0E0F0' }}>{addr || 'Not generated yet'}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
